import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import Modal from '../components/Modal.jsx';

export default function Content() {
  const [tab, setTab] = useState('wa');
  return (
    <div className="px-8 py-6 max-w-[1200px] mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Content</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage WhatsApp templates and saved email bodies — used by campaigns.</p>
      </div>

      <div className="flex border-b border-slate-200 mb-5">
        <TabButton active={tab === 'wa'}    onClick={() => setTab('wa')}>WhatsApp templates</TabButton>
        <TabButton active={tab === 'email'} onClick={() => setTab('email')}>Email templates</TabButton>
      </div>

      {tab === 'wa'    && <WaTemplates />}
      {tab === 'email' && <EmailTemplates />}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px
        ${active ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
    >{children}</button>
  );
}

/* ============================================================================
 * WhatsApp templates
 * ============================================================================ */
function WaTemplates() {
  const [rows, setRows]   = useState([]);
  const [busy, setBusy]   = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [filter, setFilter] = useState('approved'); // approved | all
  const [detail, setDetail] = useState(null);

  async function load() {
    try { const r = await api.get('/api/wa-templates'); setRows(r.rows || []); } catch {}
  }
  useEffect(() => { load(); }, []);

  async function refresh() {
    setBusy(true); setRefreshMsg('');
    try {
      const r = await api.post('/api/wa-templates/refresh');
      setRefreshMsg(`Fetched ${r.fetched} (inserted ${r.inserted}, updated ${r.updated}).`);
      await load();
    } catch (e) { setRefreshMsg('Failed: ' + (e.body?.error || e.message)); }
    finally { setBusy(false); }
  }

  const filtered = filter === 'approved' ? rows.filter(r => r.status === 'APPROVED') : rows;

  return (
    <>
      <div className="card p-4 mb-4 flex flex-wrap items-center gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Cached templates ({filtered.length})</h3>
          <p className="text-xs text-slate-500 mt-0.5">Read-only — Meta Business Manager owns the source of truth.</p>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 text-sm">
          <label className="inline-flex items-center gap-1.5 text-slate-600">
            <input type="radio" checked={filter === 'approved'} onChange={() => setFilter('approved')} /> Approved only
          </label>
          <label className="inline-flex items-center gap-1.5 text-slate-600">
            <input type="radio" checked={filter === 'all'}      onChange={() => setFilter('all')} /> All
          </label>
        </div>
        <button className="btn" onClick={refresh} disabled={busy}>{busy ? 'Refreshing…' : 'Refresh from Meta'}</button>
      </div>
      {refreshMsg && <div className="text-xs text-slate-500 mb-3">{refreshMsg}</div>}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left">Name</th>
              <th className="px-2 py-2.5 text-left">Lang</th>
              <th className="px-2 py-2.5 text-left">Status</th>
              <th className="px-2 py-2.5 text-left">Header</th>
              <th className="px-2 py-2.5 text-left">Body vars</th>
              <th className="px-2 py-2.5 text-left">URL button</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(t => (
              <tr key={t.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-900">{t.name}</td>
                <td className="px-2 py-2.5 text-slate-500">{t.language}</td>
                <td className="px-2 py-2.5"><StatusPill status={t.status} /></td>
                <td className="px-2 py-2.5 text-slate-600">{t.variables?.header_type || '—'}</td>
                <td className="px-2 py-2.5 text-slate-600">{t.variables?.body_count ?? 0}</td>
                <td className="px-2 py-2.5 text-slate-500 truncate max-w-[260px] font-mono text-[11px]" title={t.variables?.button_url_pattern || ''}>
                  {t.variables?.button_url_pattern || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-2 py-2.5 pr-4 text-right">
                  <button className="btn-ghost btn-sm" onClick={() => setDetail(t)}>View</button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No templates.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name || ''} maxWidth="max-w-3xl">
        {detail && <WaTemplateDetail t={detail} />}
      </Modal>
    </>
  );
}

function StatusPill({ status }) {
  const cls = status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700'
    : status === 'PENDING'  ? 'bg-amber-50 text-amber-700'
    : status === 'REJECTED' ? 'bg-rose-50 text-rose-700'
    : status === 'DISABLED' ? 'bg-slate-200 text-slate-500'
    : 'bg-slate-100 text-slate-600';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{status}</span>;
}

function WaTemplateDetail({ t }) {
  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center gap-3">
        <code className="px-2 py-1 bg-slate-100 rounded text-xs">{t.name}</code>
        <span className="text-slate-500">{t.language}</span>
        <StatusPill status={t.status} />
        <span className="text-slate-500">{t.category}</span>
      </div>
      {(t.components || []).map((c, i) => <ComponentBlock key={i} c={c} />)}
      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer">Raw JSON</summary>
        <pre className="mt-2 p-3 bg-slate-50 rounded-lg overflow-auto max-h-64 font-mono text-[11px]">{JSON.stringify(t.components, null, 2)}</pre>
      </details>
    </div>
  );
}

function ComponentBlock({ c }) {
  if (c.type === 'HEADER') {
    return <Block label={`Header — ${c.format}`}>
      {c.format === 'TEXT' && <pre className="whitespace-pre-wrap font-sans text-slate-700">{c.text}</pre>}
      {c.format === 'IMAGE' && <div className="text-xs text-slate-500">Image header. Send-time requires <code>image.id</code> or <code>image.link</code>.</div>}
    </Block>;
  }
  if (c.type === 'BODY') {
    return <Block label="Body">
      <pre className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed">{c.text}</pre>
      {c.example?.body_text && (
        <div className="mt-2 text-xs text-slate-500">Example: <code>{(c.example.body_text[0] || []).join(' · ')}</code></div>
      )}
    </Block>;
  }
  if (c.type === 'FOOTER') {
    return <Block label="Footer"><pre className="whitespace-pre-wrap font-sans text-slate-600 text-xs">{c.text}</pre></Block>;
  }
  if (c.type === 'BUTTONS') {
    return <Block label={`Buttons (${(c.buttons || []).length})`}>
      <ul className="space-y-1.5 text-xs">
        {(c.buttons || []).map((b, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 w-12">{b.type}</span>
            <span className="font-medium text-slate-700">{b.text}</span>
            {b.url && <code className="text-slate-500 font-mono">{b.url}</code>}
          </li>
        ))}
      </ul>
    </Block>;
  }
  return null;
}

function Block({ label, children }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

/* ============================================================================
 * Email templates
 * ============================================================================ */
function EmailTemplates() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null); // null | 'new' | { id, ... }

  async function load() {
    try { const r = await api.get('/api/email-templates'); setRows(r.rows || []); } catch {}
  }
  useEffect(() => { load(); }, []);

  async function loadDefault() {
    try { return await api.get('/api/templates/email-default'); } catch { return ''; }
  }

  async function startNew() {
    const html = await loadDefault();
    setOpen({
      id: null, name: 'IAAPI Membership Invitation',
      subject: 'Your IAAPI Membership Invitation — Exclusive Discount for Expo 2026 Attendees',
      html: typeof html === 'string' ? html : '',
    });
  }
  async function startEdit(id) {
    try { const r = await api.get(`/api/email-templates/${id}`); setOpen(r.template); } catch (e) { alert(e.message); }
  }
  async function remove(id, name) {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.del(`/api/email-templates/${id}`); load(); } catch (e) { alert(e.body?.error || e.message); }
  }

  return (
    <>
      <div className="card p-4 mb-4 flex items-center gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Saved templates ({rows.length})</h3>
          <p className="text-xs text-slate-500 mt-0.5">Reusable HTML bodies. Pick one when creating a campaign.</p>
        </div>
        <div className="flex-1" />
        <button className="btn" onClick={startNew}>+ New template</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(t => (
          <button key={t.id} className="card p-5 text-left hover:border-slate-300 transition" onClick={() => startEdit(t.id)}>
            <div className="flex items-center gap-2 mb-1.5">
              <h4 className="text-sm font-semibold text-slate-900 truncate flex-1">{t.name}</h4>
              {t.is_system ? <span className="text-[10px] uppercase tracking-wider text-slate-400">system</span> : null}
            </div>
            <div className="text-xs text-slate-600 truncate mb-2.5">{t.subject}</div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span>{t.html_len.toLocaleString()} chars</span>
              <span>·</span>
              {(t.placeholders ? t.placeholders.split(',') : []).map(p => p && (
                <span key={p} className="px-1.5 py-0.5 bg-brand-50 text-brand-700 rounded font-mono">{`{{${p}}}`}</span>
              ))}
            </div>
          </button>
        ))}
        {!rows.length && (
          <div className="md:col-span-2 card p-12 text-center">
            <div className="text-slate-400 text-sm">No templates yet.</div>
            <button className="btn mt-3" onClick={startNew}>Create your first email template</button>
          </div>
        )}
      </div>

      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={open?.id ? `Edit · ${open.name}` : 'New email template'}
        maxWidth="max-w-3xl"
        footer={
          <>
            <button className="btn-ghost" onClick={() => setOpen(null)}>Close</button>
            {open?.id && <button className="btn-danger" onClick={() => { remove(open.id, open.name); setOpen(null); }}>Delete</button>}
            <button className="btn" onClick={async () => {
              try {
                if (open.id) {
                  await api.patch(`/api/email-templates/${open.id}`, { name: open.name, subject: open.subject, html: open.html });
                } else {
                  await api.post('/api/email-templates', { name: open.name, subject: open.subject, html: open.html });
                }
                setOpen(null); load();
              } catch (e) { alert(e.body?.error || e.message); }
            }}>{open?.id ? 'Save' : 'Create'}</button>
          </>
        }
      >
        {open && <EmailEditor t={open} onChange={(patch) => setOpen(o => ({ ...o, ...patch }))} />}
      </Modal>
    </>
  );
}

function EmailEditor({ t, onChange }) {
  const placeholders = extractPlaceholders(t.html);
  const known = ['first_name', 'name', 'phone', 'email', 'org', 'designation', 'city', 'state', 'link_url'];
  return (
    <div className="space-y-3">
      <div>
        <div className="label-l">Name (internal)</div>
        <input className="input w-full" value={t.name} onChange={e => onChange({ name: e.target.value })} />
      </div>
      <div>
        <div className="label-l">Subject</div>
        <input className="input w-full" value={t.subject} onChange={e => onChange({ subject: e.target.value })} />
      </div>
      <div>
        <div className="label-l flex items-center justify-between">
          <span>HTML body</span>
          <button type="button" className="text-[11px] text-brand-600 hover:underline"
            onClick={async () => {
              try {
                const html = await api.get('/api/templates/email-default');
                onChange({ html: typeof html === 'string' ? html : '' });
              } catch (e) { alert(e.message); }
            }}>
            Reset to IAAPI default
          </button>
        </div>
        <textarea className="input w-full h-72 font-mono text-xs" value={t.html} onChange={e => onChange({ html: e.target.value })} />
      </div>
      <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Placeholders found</div>
        <div className="flex flex-wrap gap-1.5">
          {placeholders.length
            ? placeholders.map(p => (
              <span key={p} className={`px-2 py-0.5 rounded text-[11px] font-mono ${known.includes(p) ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} title={known.includes(p) ? 'Auto-filled at send-time' : 'Unknown placeholder — will be left blank'}>
                {`{{${p}}}`}
              </span>
            ))
            : <span className="text-xs text-slate-400">No placeholders.</span>}
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          Auto-filled names: <code>first_name</code>, <code>name</code>, <code>phone</code>, <code>email</code>, <code>org</code>, <code>designation</code>, <code>city</code>, <code>state</code>, <code>link_url</code>.
        </div>
      </div>
    </div>
  );
}

function extractPlaceholders(html) {
  const set = new Set();
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(html || '')) !== null) set.add(m[1]);
  return [...set];
}
